import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import ExcelUpload from "./ExcelUpload";
import { API_BASE_URL } from "../../config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ModalComponent from "./ModalComponent";
import {
  FaFileAlt,
  FaPhone,
  FaComment,
  FaCalendar,
  FaUpload,
  FaFilePdf,
  FaDownload,
} from "react-icons/fa";
import { MdRefresh } from "react-icons/md";
import { useDropzone } from "react-dropzone";
import DemoWarning from "./Whatsapp components/DemoWarning";
import Notification from "./Whatsapp components/Notification";
import ApiKeySection from "./Whatsapp components/ApiKeySection";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { motion } from "framer-motion";
import ProcessingLoader from "./Whatsapp components/ProcessingLoader";
import Layout from "../Layout/Layout";
import { useTheme } from "../../contexts/ThemeContext";
import WhatsAppLivePreview from "../shared/WhatsAppLivePreview.jsx";

// Footer Component
const Footer = () => {
  const { theme } = useTheme();
  return (
    <motion.footer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`${theme.footerBg} text-white py-4 text-center shadow-inner`}
    >
      <p>Â© {new Date().getFullYear()} Smart ItBox. All rights reserved.</p>
    </motion.footer>
  );
};

// Main WhatsAppPage Component
function WhatsAppPage() {
  const [apiKey, setApiKey] = useState("");
  const [receiverMobileNo, setReceiverMobileNo] = useState("");
  const [message, setMessage] = useState("");
  const [filePathUrl, setFilePathUrl] = useState("");
  const [existingApiKey, setExistingApiKey] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [extractedData, setExtractedData] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [scheduleTime, setScheduleTime] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [templateId, setTemplateId] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [apiKeyExpired, setApiKeyExpired] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [userId, setUserId] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [notification, setNotification] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputFocused, setInputFocused] = useState(false); // NEW: track input focus
  const [templates, setTemplates] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showDemoWarning, setShowDemoWarning] = useState(false);
  const [accountType, setAccountType] = useState("");
  const [remainingTime, setRemainingTime] = useState("");
  const [error, setError] = useState("");
  const [selectedPreviewContactIndex, setSelectedPreviewContactIndex] =
    useState(0);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const { theme } = useTheme();
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + "px";
    }
  }, [message]);

  // Handlers and Effects (unchanged functionality)
  const handleCloseWarning = () => {
    setShowDemoWarning(false);
    setTimeout(() => setShowDemoWarning(true), 10000);
  };

  const handleCloseNotification = () => {
    setShowNotification(false);
    setTimeout(() => setShowNotification(true), 5000);
  };

  const handleEmojiSelect = (emoji) => {
    setMessage((prev) => prev + emoji.native);
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      const token = window.__appSession.getToken();
      try {
        const response = await axios.get(`${API_BASE_URL}/api/templates`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTemplates(response.data);
      } catch (error) {
        console.error("Error fetching templates:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const handleTemplateSelect = (name) => {
    setTemplateName(name);
    setShowDropdown(false);
  };

  const handleRefresh = async () => {
    setIsUpdating(true);
    setNotification("");
    const token = window.__appSession.getToken();
    if (!token) {
      setNotification("Please log in to update your profiles.");
      setIsUpdating(false);
      return;
    }
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/update-user/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setNotification("User profiles updated successfully!");
      window.location.reload();
    } catch (error) {
      setNotification(
        error.response?.data?.message || "Failed to update user profiles.",
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleVisibility = () => setIsVisible(!isVisible);

  const displayValue = () => {
    if (apiKey.length <= 8) return apiKey;
    const start = apiKey.slice(0, 4);
    const end = apiKey.slice(-4);
    const maskedLength = apiKey.length - 8;
    const masked = "x".repeat(maskedLength);
    return `${start}${masked}${end}`;
  };

  const handleSendMessageWithTemplateSingle = async (
    templateName,
    mobileNo,
  ) => {
    setLoading(true);
    const token = window.__appSession.getToken();
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/templates/name/${templateName}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const { text, imageUrls } = response.data;
      await axios.post(
        `${API_BASE_URL}/api/whatsapp/send-message`,
        {
          receiverMobileNo: mobileNo,
          message: [text],
          filePathUrl: imageUrls,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Message sent to ${mobileNo}!`);
    } catch (error) {
      toast.error("Failed to send message.");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to personalize message based on template's salutation setting
  // Always replaces {name} placeholder with actual name from Excel data if name is provided
  // Works with both old templates (without hasSalutation field) and new ones
  const personalizeMessage = (templateText, name, hasSalutation) => {
    // If no name provided, return message as is (don't replace placeholder)
    if (!name || name.trim() === "") {
      return templateText;
    }

    // Always replace {name} placeholder(s) with actual name
    // This works regardless of hasSalutation flag for backward compatibility
    const personalizedText = templateText.replace(/\{name\}/g, name.trim());

    return personalizedText;
  };

  const handleSendMessageWithTemplate = async (templateName, extractedData) => {
    setLoading(true);
    setIsProcessing(true);
    setProgress(0);
    const token = window.__appSession.getToken();
    const processedNumbers = new Set();
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/templates/name/${templateName}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const { text, imageUrls, hasSalutation } = response.data;
      const totalMessages = extractedData.length;
      for (let i = 0; i < totalMessages; i++) {
        const item = extractedData[i];
        let mobileNo =
          typeof item[0] === "number" ? item[0].toString() : item[0];
        const name = item[1] ? String(item[1]).trim() : "";
        const personalizedMessage = personalizeMessage(
          text,
          name,
          hasSalutation,
        );
        if (processedNumbers.has(mobileNo)) {
          toast.error(`Duplicate phone number found for ${mobileNo}.`);
          continue;
        }
        processedNumbers.add(mobileNo);
        await axios.post(
          `${API_BASE_URL}/api/whatsapp/send-message`,
          {
            receiverMobileNo: mobileNo,
            message: [personalizedMessage],
            filePathUrl: imageUrls,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setProgress(((i + 1) / totalMessages) * 100);
        toast.success(`Message sent to ${mobileNo}!`);
      }
    } catch (error) {
      toast.error("Failed to send message.");
    } finally {
      setLoading(false);
      setIsProcessing(false);
      setProgress(0);
    }
  };

  const handleScheduleMessageWithTemplate = async (
    templateName,
    scheduleTime,
    extractedData,
  ) => {
    if (!scheduleTime) {
      toast.error("Please select a scheduled time.");
      return;
    }
    setLoading(true);
    setIsProcessing(true);
    setProgress(0);
    const token = window.__appSession.getToken();
    const processedNumbers = new Set();
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/templates/name/${templateName}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const { text, imageUrls, hasSalutation } = response.data;
      const utcDateTime = new Date(scheduleTime).toISOString();
      const totalMessages = extractedData.length;
      for (let i = 0; i < totalMessages; i++) {
        const item = extractedData[i];
        let mobileNo =
          typeof item[0] === "number" ? item[0].toString() : item[0];
        const name = item[1];
        const personalizedMessage = personalizeMessage(
          text,
          name,
          hasSalutation,
        );
        if (processedNumbers.has(mobileNo)) {
          toast.error(`Duplicate phone number found for ${mobileNo}.`);
          continue;
        }
        processedNumbers.add(mobileNo);
        await axios.post(
          `${API_BASE_URL}/api/whatsapp/schedule-message`,
          {
            receiverMobileNo: mobileNo,
            message: personalizedMessage,
            scheduledTime: utcDateTime,
            filePathUrl: imageUrls,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setProgress(((i + 1) / totalMessages) * 100);
        toast.success(`Message scheduled for ${mobileNo}!`);
      }
      toast.success("All messages scheduled successfully!");
    } catch (error) {
      toast.error("Failed to schedule some messages.");
    } finally {
      setLoading(false);
      setIsProcessing(false);
      setProgress(0);
      setModalIsOpen(false);
    }
  };

  const mergeDateAndTimeToUTC = (dateString, timeString) => {
    const parts = dateString.split("-");
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    const [hours, minutes] = timeString
      .split(":")
      .map((num) => parseInt(num, 10));
    const localDate = new Date(year, month, day, hours, minutes, 0);
    return new Date(
      localDate.getTime() - localDate.getTimezoneOffset() * 60000,
    );
  };

  const handleScheduleMessageWithTemplateAndDate = async (
    templateName,
    extractedData,
  ) => {
    setLoading(true);
    setIsProcessing(true);
    setProgress(0);
    const token = window.__appSession.getToken();
    const processedNumbers = new Set();
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/templates/name/${templateName}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const { text, imageUrls, hasSalutation } = response.data;
      const totalMessages = extractedData.length;
      for (let i = 0; i < totalMessages; i++) {
        const item = extractedData[i];
        let mobileNo =
          typeof item[0] === "number" ? item[0].toString() : item[0];
        const name = item[1];
        const date = item[2];
        let time = item[3] || "09:00";
        if (processedNumbers.has(mobileNo)) {
          toast.error(`Duplicate phone number found for ${mobileNo}.`);
          continue;
        }
        processedNumbers.add(mobileNo);
        const scheduledDate = mergeDateAndTimeToUTC(date, time);
        if (!scheduledDate) {
          toast.error(`Invalid date or time for ${name}: ${date} ${time}`);
          continue;
        }
        const personalizedMessage = personalizeMessage(
          text,
          name,
          hasSalutation,
        );
        const formattedDate = scheduledDate.toISOString().split("T")[0];
        const utcTime = scheduledDate.toUTCString().split(" ")[4].slice(0, 5);
        await axios.post(
          `${API_BASE_URL}/api/whatsapp/schedule-recurring-message`,
          {
            receiverMobileNo: mobileNo,
            message: personalizedMessage,
            scheduledDate: formattedDate,
            time: utcTime,
            filePathUrl: imageUrls,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setProgress(((i + 1) / totalMessages) * 100);
        toast.success(`Recurring message scheduled for ${mobileNo}!`);
      }
      toast.success("All recurring messages scheduled successfully!");
    } catch (error) {
      toast.error("Failed to schedule some recurring messages.");
    } finally {
      setLoading(false);
      setIsProcessing(false);
      setProgress(0);
      setModalIsOpen(false);
    }
  };

  const handleScheduleMessagesTemplateFromExcel = async () => {
    await handleScheduleMessageWithTemplateAndDate(templateName, extractedData);
  };

  const handleScheduleSingleMessage = async () => {
    if (!receiverMobileNo || !message.trim() || !scheduleTime) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    setIsProcessing(true);
    setProgress(0);
    const token = window.__appSession.getToken();
    let fileUrls = [];
    const utcDateTime = new Date(scheduleTime).toISOString();
    if (files.length > 0) {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      try {
        const uploadResponse = await axios.post(
          `${API_BASE_URL}/api/whatsapp/upload`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              setProgress(percentCompleted);
            },
          },
        );
        fileUrls = uploadResponse.data.filePathUrls;
      } catch (error) {
        toast.error("Failed to upload files.");
        setLoading(false);
        setIsProcessing(false);
        setProgress(0);
        return;
      }
    }
    try {
      await axios.post(
        `${API_BASE_URL}/api/whatsapp/schedule-message`,
        {
          receiverMobileNo: receiverMobileNo,
          message,
          scheduledTime: utcDateTime,
          filePathUrl: fileUrls,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Message scheduled successfully!");
    } catch (error) {
      toast.error("Failed to schedule the message.");
    } finally {
      setLoading(false);
      setIsProcessing(false);
      setProgress(0);
      setReceiverMobileNo("");
      setMessage("");
      setFiles([]);
    }
  };

  const handleScheduleMessages = async () => {
    if (!scheduleTime) {
      toast.error("Please select a scheduled time.");
      return;
    }
    await handleScheduleAllMessages(templateName, scheduleTime);
  };

  const handleScheduleAllMessages = async (templateName, scheduleTime) => {
    if (extractedData.length === 0 || !scheduleTime) {
      toast.error("Please select a scheduled time.");
      return;
    }
    setLoading(true);
    setIsProcessing(true);
    setProgress(0);
    const token = window.__appSession.getToken();
    let message = "";
    let fileUrls = [];
    const utcDateTime = new Date(scheduleTime).toISOString();
    const processedNumbers = new Set();
    let templateHasSalutation = false;
    try {
      if (templateName) {
        const response = await axios.get(
          `${API_BASE_URL}/api/templates/name/${templateName}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const { text, imageUrls, hasSalutation } = response.data;
        message = text;
        templateHasSalutation = hasSalutation || false;
        fileUrls = imageUrls || [];
      } else {
        message = message.trim();
        if (!message) {
          toast.error("Please enter a custom message or select a template.");
          return;
        }
      }
      if (files.length > 0) {
        const formData = new FormData();
        files.forEach((file) => formData.append("files", file));
        try {
          const uploadResponse = await axios.post(
            `${API_BASE_URL}/api/whatsapp/upload`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${token}`,
              },
              onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total,
                );
                setProgress(percentCompleted / 2);
              },
            },
          );
          fileUrls = uploadResponse.data.filePathUrls;
        } catch (error) {
          toast.error("Failed to upload files.");
          setLoading(false);
          setIsProcessing(false);
          setProgress(0);
          return;
        }
      }
      const totalMessages = extractedData.length;
      for (let i = 0; i < totalMessages; i++) {
        const item = extractedData[i];
        let mobileNo =
          typeof item[0] === "number" ? item[0].toString() : item[0];
        const name = item[1];
        const personalizedMessage = personalizeMessage(
          message,
          name,
          templateHasSalutation,
        );
        if (processedNumbers.has(mobileNo)) {
          toast.error(`Duplicate phone number found for ${mobileNo}.`);
          continue;
        }
        processedNumbers.add(mobileNo);
        await axios.post(
          `${API_BASE_URL}/api/whatsapp/schedule-message`,
          {
            receiverMobileNo: mobileNo,
            message: personalizedMessage,
            scheduledTime: utcDateTime,
            filePathUrl: fileUrls,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setProgress(50 + ((i + 1) / totalMessages) * 50);
        toast.success(`Message scheduled for ${mobileNo}!`);
      }
      toast.success("All messages scheduled successfully!");
    } catch (error) {
      toast.error("Failed to schedule some messages.");
    } finally {
      setLoading(false);
      setIsProcessing(false);
      setProgress(0);
      setModalIsOpen(false);
    }
  };

  useEffect(() => {
    const fetchApiKey = async () => {
      try {
        const token = window.__appSession.getToken();
        const response = await axios.get(
          `${API_BASE_URL}/api/whatsapp/api-key`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (response.data.apiKey) {
          setExistingApiKey(response.data.apiKey);
          setApiKey(response.data.apiKey);
          window.__appSession.setItem("apiKey", response.data.apiKey);
        }
      } catch (error) {
        console.error("Error fetching API key:", error);
      }
    };
    fetchApiKey();
  }, []);

  const calculateTimeRemaining = (expiryDate) => {
    const activeUptoDate = new Date(expiryDate);
    const currentTime = new Date();
    const timeDiff = activeUptoDate - currentTime;
    if (timeDiff <= 0) return "Expired";
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    );
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  };

  useEffect(() => {
    const fetchProfileData = async () => {
      const token = window.__appSession.getToken();
      try {
        const response = await axios.get(`${API_BASE_URL}/api/auth/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.result) {
          const profileData = response.data.result;
          setUserId(profileData._id);
          setAccountType(profileData.accountType);
          if (profileData.accountType === "Demo") {
            setShowDemoWarning(true);
            const interval = setInterval(() => {
              setRemainingTime(calculateTimeRemaining(profileData.activeUpto));
            }, 1000);
            return () => clearInterval(interval);
          } else {
            setShowDemoWarning(false);
          }
          if (
            profileData.accountType === null ||
            (profileData.accountType === "No data" &&
              profileData.activeUpto === null) ||
            profileData.activeUpto === "No data"
          ) {
            setShowNotification(true);
          } else {
            setShowNotification(false);
          }
        }
      } catch (error) {
        console.error("Error fetching profile data:", error);
      }
    };
    fetchProfileData();
  }, []);

  const handleSaveApiKey = async () => {
    try {
      const token = window.__appSession.getToken();
      await axios.post(
        `${API_BASE_URL}/api/whatsapp/api-key`,
        { apiKey },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("API key saved successfully!");
      setExistingApiKey(apiKey);
      window.__appSession.setItem("apiKey", apiKey);
    } catch (error) {
      toast.error("Error saving API key.");
    }
  };

  const handleSendMessage = async (mobileNo, personalizedMessage) => {
    const token = window.__appSession.getToken();
    let fileUrls = [];
    setLoading(true);
    setIsProcessing(true);
    setProgress(0);
    if (files.length > 0) {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      try {
        const uploadResponse = await axios.post(
          `${API_BASE_URL}/api/whatsapp/upload`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              setProgress(percentCompleted);
            },
          },
        );
        fileUrls = uploadResponse.data.filePathUrls;
      } catch (error) {
        toast.error("Failed to upload files.");
        setLoading(false);
        setIsProcessing(false);
        setProgress(0);
        return;
      }
    }
    try {
      await axios.post(
        `${API_BASE_URL}/api/whatsapp/send-message`,
        {
          receiverMobileNo: mobileNo,
          message: [personalizedMessage],
          filePathUrl: fileUrls,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await axios.post(
        `${API_BASE_URL}/api/whatsapp/update-message-status`,
        { mobileNo, message: personalizedMessage, status: "sent" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Message sent to ${mobileNo}!`);
    } catch (error) {
      toast.error(`Failed to send message to ${mobileNo}.`);
    } finally {
      setLoading(false);
      setIsProcessing(false);
      setProgress(0);
    }
  };

  const handleNumbersExtracted = (data) => {
    setExtractedData(data);
    setModalIsOpen(true);
  };

  const handleSendAllMessages = async () => {
    if (extractedData.length === 0 || !message.trim() || isProcessing) return;
    setIsProcessing(true);
    setProgress(0);
    let fileUrls = [];
    const processedNumbers = new Set();
    const token = window.__appSession.getToken();
    const validData = [];

    if (files.length > 0) {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      try {
        const uploadResponse = await axios.post(
          `${API_BASE_URL}/api/whatsapp/upload`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              setProgress(percentCompleted / 2);
            },
          },
        );
        fileUrls = uploadResponse.data.filePathUrls;
      } catch (error) {
        toast.error("Failed to upload files.");
        setIsProcessing(false);
        setProgress(0);
        return;
      }
    }

    try {
      await axios.post(
        `${API_BASE_URL}/api/whatsapp/save-message`,
        { message, extractedData, filePathUrl: fileUrls },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      for (let i = 0; i < extractedData.length; i++) {
        const item = extractedData[i];
        let mobileNo =
          typeof item[0] === "number" ? item[0].toString() : item[0];
        const name = item[1];
        if (!mobileNo || !name) continue;
        mobileNo = mobileNo.trim().replace(/\D/g, "");
        if (processedNumbers.has(mobileNo)) {
          toast.error(`Duplicate phone number found for ${mobileNo}.`);
          continue;
        }
        processedNumbers.add(mobileNo);
        validData.push({ mobileNo, name });
      }

      if (validData.length === 0) {
        toast.error("No valid phone numbers found.");
        setIsProcessing(false);
        setProgress(0);
        return;
      }

      const totalMessages = validData.length;
      for (let i = 0; i < totalMessages; i++) {
        const item = validData[i];
        const personalizedMessage = `Dear ${item.name},\n\n${message}`;
        await axios.post(
          `${API_BASE_URL}/api/whatsapp/save-message`,
          { message, extractedData: [item], filePathUrl: fileUrls },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        await handleSendMessage(item.mobileNo, personalizedMessage);
        setProgress(50 + ((i + 1) / totalMessages) * 50);
      }
      toast.success("All valid messages processed!");
    } catch (error) {
      toast.error("Failed to process some messages.");
    } finally {
      setIsProcessing(false);
      setProgress(0);
      setModalIsOpen(false);
    }
  };

  const MAX_SIZE = 25 * 1024 * 1024;
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        setError("File size exceeds the 25MB limit or invalid file type.");
      } else {
        setFiles(acceptedFiles);
        setError("");
      }
    },
    accept: "image/*,application/pdf",
    maxSize: MAX_SIZE,
  });

  const generatePreview = (file) => {
    const fileType = file.type.split("/")[0];
    const previewUrl = URL.createObjectURL(file);
    if (fileType === "image")
      return (
        <img
          src={previewUrl}
          alt={file.name}
          className="w-20 h-20 object-cover rounded-lg shadow-md"
        />
      );
    if (fileType === "video")
      return (
        <video
          src={previewUrl}
          controls
          className="w-20 h-20 object-cover rounded-lg shadow-md"
        >
          Your browser does not support the video tag.
        </video>
      );
    if (fileType === "application" && file.name.endsWith(".pdf"))
      return (
        <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-lg shadow-md">
          <FaFileAlt className="text-gray-500 text-2xl" />
        </div>
      );
    return null;
  };

  // Filter templates based on input
  const filteredTemplates = templateName
    ? templates.filter((template) =>
        template.templateName
          .toLowerCase()
          .includes(templateName.toLowerCase()),
      )
    : templates;

  const bulkContacts = extractedData.filter((item) => item[0] && item[1]);
  const selectedBulkContact =
    bulkContacts[Math.min(selectedPreviewContactIndex, bulkContacts.length - 1)] ||
    null;

  const selectedTemplate = templates.find(
    (template) =>
      template.templateName?.toLowerCase() === templateName.trim().toLowerCase(),
  );

  const previewReceiverName = selectedBulkContact?.[1]
    ? String(selectedBulkContact[1]).trim()
    : "";

  const previewReceiverMobile = selectedBulkContact?.[0]
    ? String(selectedBulkContact[0])
    : receiverMobileNo;

  const previewTemplateText = selectedTemplate?.text || "";

  const previewMessage = (() => {
    if (selectedTemplate?.text) {
      return personalizeMessage(
        previewTemplateText,
        previewReceiverName,
        selectedTemplate?.hasSalutation,
      );
    }

    if (selectedBulkContact && message.trim()) {
      return `Dear ${previewReceiverName},\n\n${message}`;
    }

    return message;
  })();

  const previewAttachments =
    files.length > 0 ? files : selectedTemplate?.imageUrls || [];

  const getRowPreviewText = (row) => {
    const rowName = row?.[1] ? String(row[1]).trim() : "";

    if (selectedTemplate?.text) {
      return personalizeMessage(
        selectedTemplate.text,
        rowName,
        selectedTemplate?.hasSalutation,
      );
    }

    if (!message.trim()) {
      return "No message";
    }

    return rowName ? `Dear ${rowName},\n\n${message}` : message;
  };

  return (
    <Layout>
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="w-full flex-grow"
      >
        <div className="w-full">
          <DemoWarning
            showDemoWarning={showDemoWarning}
            remainingTime={remainingTime}
            handleCloseWarning={handleCloseWarning}
          />
          <Notification
            showNotification={showNotification}
            handleCloseNotification={handleCloseNotification}
            handleRefresh={handleRefresh}
            isUpdating={isUpdating}
          />

          <div className="grid w-full grid-cols-1 gap-3">
            {/* Main Form Section */}
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}
              className={`glass-surface-strong ${theme.contentBg} relative rounded-2xl border border-indigo-200/70 p-4 md:p-5`}
            >
              <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                <div>
                  <h1 className="bg-gradient-to-r from-indigo-700 via-violet-700 to-indigo-700 bg-clip-text text-xl font-bold text-transparent md:text-2xl">
                    Compose WhatsApp Message
                  </h1>
                  <p className="mt-1 text-xs text-slate-600 md:text-sm">
                    Send instantly, schedule confidently, and preview every message before delivery.
                  </p>
                </div>
                <div className="inline-flex items-center rounded-full border border-indigo-200 bg-white/75 px-2.5 py-1 text-[11px] font-semibold text-indigo-700">
                  Powered by templates + bulk upload
                </div>
              </div>

              <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
                <div>
                  <ApiKeySection
                    isVisible={isVisible}
                    apiKey={apiKey}
                    handleToggleVisibility={handleToggleVisibility}
                    setApiKey={setApiKey}
                    handleSaveApiKey={handleSaveApiKey}
                    displayValue={displayValue}
                    showDemoWarning={showDemoWarning}
                    handleRefresh={handleRefresh}
                    isUpdating={isUpdating}
                    theme={theme}
                  />

                  <ProcessingLoader
                    isProcessing={isProcessing}
                    progress={progress}
                  />

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (
                      templateName &&
                      scheduleTime &&
                      extractedData.length > 0
                    ) {
                      handleScheduleMessageWithTemplate(
                        templateName,
                        scheduleTime,
                        extractedData,
                      );
                    } else if (templateName && extractedData.length > 0) {
                      handleSendMessageWithTemplate(templateName, extractedData);
                    } else if (templateName) {
                      handleSendMessageWithTemplateSingle(
                        templateName,
                        receiverMobileNo,
                      );
                    } else if (scheduleTime) {
                      handleScheduleSingleMessage();
                    } else {
                      if (extractedData.length > 0) {
                        handleSendAllMessages();
                      } else {
                        handleSendMessage(receiverMobileNo, message);
                      }
                    }
                    setReceiverMobileNo("");
                    setMessage("");
                    setTemplateName("");
                    setFiles([]);
                    setScheduleTime("");
                  }}
                  className="w-full space-y-4 rounded-2xl border border-indigo-200/70 bg-white/70 p-4 backdrop-blur"
                >
                  <h3 className="mb-1 text-lg font-semibold text-slate-900">
                    Send WhatsApp Message
                  </h3>
                  <p className="text-xs text-slate-500">
                    Use mobile number for single send, or upload Excel for bulk actions.
                  </p>

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    {/* Mobile Number */}
                    <motion.div
                    whileFocus={{ scale: 1.02 }}
                    className="relative flex items-center gap-2 rounded-xl border border-indigo-200 bg-white/90 px-2 py-1"
                  >
                    <FaPhone className="ml-1 text-sm text-indigo-600" />
                    <input
                      type="tel"
                      placeholder="Mobile Number"
                      value={receiverMobileNo}
                      onChange={(e) => setReceiverMobileNo(e.target.value)}
                      className={`w-full rounded-lg bg-transparent p-2 text-xs text-slate-700 transition-all focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                      disabled={isProcessing || showNotification}
                    />
                    </motion.div>

                    {/* Template Name */}
                    <motion.div
                    whileFocus={{ scale: 1.02 }}
                    className="relative flex items-center gap-2 rounded-xl border border-indigo-200 bg-white/90 px-2 py-1"
                  >
                    <FaFileAlt className="ml-1 text-sm text-indigo-600" />
                    <input
                      type="text"
                      placeholder="Template Name"
                      value={templateName}
                      onChange={(e) => {
                        setTemplateName(e.target.value);
                        setShowDropdown(true); // Show dropdown as user types
                      }}
                      onFocus={() => {
                        setInputFocused(true);
                        setShowDropdown(true);
                      }}
                      onBlur={() => {
                        // Delay hiding dropdown to allow click selection
                        setTimeout(() => {
                          setInputFocused(false);
                          setShowDropdown(false);
                        }, 150);
                      }}
                      className={`w-full rounded-lg bg-transparent p-2 text-xs text-slate-700 transition-all focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                      disabled={isProcessing || showNotification}
                    />
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      type="button"
                      onClick={() => setShowDropdown((prev) => !prev)}
                      className="absolute right-2 text-sm text-slate-500"
                      tabIndex={-1}
                    >
                      â–¼
                    </motion.button>
                    {showDropdown && filteredTemplates.length > 0 && (
                      <motion.ul
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="absolute top-full z-10 mt-1 max-h-36 w-full overflow-y-auto rounded-xl border border-indigo-200 bg-white shadow-md"
                      >
                        {filteredTemplates.map((template) => (
                          <motion.li
                            key={template._id}
                            whileHover={{ backgroundColor: "#eef2ff" }}
                            onMouseDown={() => {
                              handleTemplateSelect(template.templateName);
                              setShowDropdown(false);
                            }}
                            className="cursor-pointer p-2 text-xs text-slate-700"
                          >
                            {template.templateName}
                          </motion.li>
                        ))}
                      </motion.ul>
                    )}
                    </motion.div>

                    {/* Schedule Time */}
                    <motion.div
                    whileFocus={{ scale: 1.02 }}
                    className="relative flex items-center gap-2 rounded-xl border border-indigo-200 bg-white/90 px-2 py-1"
                  >
                    <FaCalendar className="ml-1 text-sm text-indigo-600" />
                    <input
                      type="datetime-local"
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                      className={`w-full rounded-lg bg-transparent p-2 text-xs text-slate-700 transition-all focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                      disabled={showNotification}
                    />
                    </motion.div>

                    {/* Message with Emoji Picker */}
                    <div className="relative sm:col-span-full">
                    <motion.div
                      whileFocus={{ scale: 1.02 }}
                      className="flex items-start gap-2 rounded-xl border border-indigo-200 bg-white/90 px-2 py-1"
                    >
                      <FaComment
                        className="ml-1 mt-2 text-sm text-indigo-600"
                      />
                      <textarea
                        ref={textareaRef}
                        placeholder="Your Message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        className={`w-full resize-y rounded-lg bg-transparent p-2 text-xs text-slate-700 transition-all focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                        disabled={isProcessing || showNotification}
                      />
                      {/* <motion.button
                        whileHover={{ scale: 1.1 }}
                        type="button"
                        onClick={() => setShowEmojiPicker((prev) => !prev)}
                        className="absolute right-2 top-2 text-gray-500 text-sm"
                      >
                        ðŸ˜€
                      </motion.button> */}
                    </motion.div>
                    {/* {showEmojiPicker && (
                      <motion.div
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 5 }}
                        className={`absolute left-0 right-0 ${theme.contentBg} border ${theme.contentBorder} rounded-md shadow-md p-1 z-10 mt-1`}
                      >
                        <EmojiPickerComponent
                          onEmojiSelect={(emoji) => {
                            handleEmojiSelect(emoji);
                            setShowEmojiPicker(false);
                          }}
                          showEmojis={showEmojiPicker}
                        />
                      </motion.div>
                    )} */}
                    </div>
                  </div>

                  {/* File Upload */}
                  <div className="relative">
                  <motion.div
                    whileHover={{ borderColor: theme.iconColor }}
                    {...getRootProps()}
                    className="flex cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-indigo-200 bg-white/80 p-3 text-xs text-slate-600 transition-all hover:bg-indigo-50/60"
                  >
                    <FaUpload className="mr-1 text-sm text-indigo-600" />
                    <input {...getInputProps()} />
                    <p>Upload Files (Max 25MB)</p>
                  </motion.div>
                  {error && (
                    <p className="text-red-500 text-xs mt-1">{error}</p>
                  )}
                  {files.length > 0 && (
                    <div className="mt-1 flex gap-1 flex-wrap">
                      {files.map((file, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="relative group"
                        >
                          {generatePreview(file)}
                          <p className="mt-1 w-14 text-center text-xs text-slate-600">
                            {file.name}
                          </p>
                        </motion.div>
                      ))}
                    </div>
                  )}
                  </div>

                  {/* {bulkContacts.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-2 items-center">
                      <label className="text-xs font-semibold text-slate-600">
                        Preview Contact
                      </label>
                      <select
                        value={selectedPreviewContactIndex}
                        onChange={(e) =>
                          setSelectedPreviewContactIndex(
                            Number.parseInt(e.target.value, 10),
                          )
                        }
                        className={`w-full rounded-xl border border-indigo-200 bg-white/90 p-2 text-xs text-slate-700 focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                      >
                        {bulkContacts.map((item, index) => (
                          <option key={`${item[0]}-${index}`} value={index}>
                            {item[1]} ({item[0]})
                          </option>
                        ))}
                      </select>
                    </div>
                  )} */}

                  {/* Submit Button */}
                  <div className="flex justify-between gap-2 items-center">
                    <button
                      type="button"
                      onClick={() => setShowMobilePreview(true)}
                      className="lg:hidden rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-indigo-100"
                    >
                      Open Live Preview
                    </button>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="submit"
                    className="w-fit rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2 text-[12px] font-medium text-white shadow-sm transition-all hover:opacity-95"
                    disabled={isProcessing}
                  >
                    {isProcessing
                      ? "Processing..."
                      : scheduleTime
                        ? "Schedule Message"
                        : "Send Message"}
                  </motion.button>
                  </div>
                </form>

                <div className="mt-4 rounded-2xl border border-indigo-200 bg-gradient-to-r from-indigo-50/80 via-violet-50/70 to-indigo-50/80 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-900">Bulk Actions</h4>
                    <span className="text-[11px] text-slate-500">
                      {extractedData.length} record(s) loaded
                    </span>
                  </div>

                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-5">
                    <ExcelUpload
                      onNumbersExtracted={handleNumbersExtracted}
                      buttonClassName="w-full h-full inline-flex items-center justify-center rounded-xl border border-indigo-500/30 bg-gradient-to-r from-indigo-600 to-violet-600 px-3 py-2 text-xs font-semibold text-white hover:opacity-95"
                      buttonLabel={<><FaUpload className="mr-2" />Exc Upload</>}
                    />

                    <motion.a
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      href="/WhatsappDemo.xlsx"
                      download
                      className="inline-flex h-full w-full items-center justify-center rounded-xl border border-indigo-200 bg-white px-3 py-2 text-center text-xs font-medium text-slate-700 transition-all hover:bg-indigo-50"
                    >
                      <FaDownload className="mr-2 text-indigo-500" size={12} />
                      {/* <FaFilePdf className="mr-2 text-rose-500" size={12} /> */}
                      Sample
                    </motion.a>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setModalIsOpen(true)}
                      className="inline-flex h-full w-full items-center justify-center rounded-xl border border-indigo-200 bg-white px-3 py-2 text-center text-xs font-medium text-slate-700 transition-all hover:bg-indigo-50"
                    >
                      <FaFileAlt className="mr-2 text-indigo-500" />
                      Details
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleScheduleMessages}
                      className="inline-flex h-full w-full items-center justify-center rounded-xl border border-indigo-500/30 bg-gradient-to-r from-indigo-600 to-violet-600 px-3 py-2 text-center text-xs font-medium text-white transition-all hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={extractedData.length === 0 || isProcessing}
                    >
                      <FaCalendar className="mr-2" />
                      Sched. Exc Msg
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleScheduleMessagesTemplateFromExcel}
                      className="inline-flex h-full w-full items-center justify-center rounded-xl border border-indigo-500/30 bg-gradient-to-r from-indigo-600 to-violet-600 px-3 py-2 text-center text-xs font-medium text-white transition-all hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={extractedData.length === 0 || isProcessing}
                    >
                      <FaCalendar className="mr-2" />
                      B'day Sched.
                    </motion.button>
                  </div>
                </div>
                </div>

                <div className="hidden lg:block sticky top-20">
                  <WhatsAppLivePreview
                    title="WhatsApp Preview"
                    receiverMobileNo={previewReceiverMobile}
                    receiverName={previewReceiverName}
                    message={previewMessage}
                    scheduleTime={scheduleTime}
                    attachments={previewAttachments}
                  />
                </div>
              </div>

              <ToastContainer />

              {showMobilePreview && (
                <div className="fixed inset-0 z-50 lg:hidden">
                  <div
                    className="absolute inset-0 bg-black/45"
                    onClick={() => setShowMobilePreview(false)}
                  />
                  <div className="absolute bottom-0 left-0 right-0 max-h-[78vh] overflow-y-auto rounded-t-2xl border border-indigo-200 bg-white/95 p-3 shadow-2xl backdrop-blur">
                    <div className="mb-2 flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-slate-800">
                        Live Preview
                      </h4>
                      <button
                        type="button"
                        onClick={() => setShowMobilePreview(false)}
                        className="rounded border border-indigo-200 px-2 py-1 text-xs text-slate-700"
                      >
                        Close
                      </button>
                    </div>
                    <WhatsAppLivePreview
                      title="WhatsApp Preview"
                      receiverMobileNo={previewReceiverMobile}
                      receiverName={previewReceiverName}
                      message={previewMessage}
                      scheduleTime={scheduleTime}
                      attachments={previewAttachments}
                    />
                  </div>
                </div>
              )}
            </motion.div>
          </div>

          <ModalComponent
            isOpen={modalIsOpen}
            onRequestClose={() => setModalIsOpen(false)}
            extractedData={extractedData}
            setExtractedData={setExtractedData}
            handleSendAllMessages={handleSendAllMessages}
            handleScheduleAllMessages={handleScheduleAllMessages}
            getRowPreviewText={getRowPreviewText}
          />
        </div>
      </motion.main>
    </Layout>
  );
}

export default WhatsAppPage;
